import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Select, Textarea, SubmitButton, usePageMeta } from "@/components/shared";
import { submitLead } from "@/lib/api";

const SERVICE_OPTIONS = ["Co-hosting", "Full management", "Cleaning", "Home cleaning", "Maintenance", "Inspections", "Airbnb setup", "Website design", "Social media", "Interior design"];
const PARISHES = ["St. George's", "Hamilton Parish", "Smith's", "Devonshire", "Pembroke", "Paget", "Warwick", "Southampton", "Sandys"];
const PROPERTY_TYPES = ["House / Villa", "Apartment / Condo", "Cottage", "Studio", "Multiple Units", "Other"];
const TIMELINES = ["Immediately", "Within 1 month", "1–3 months", "3–6 months", "Just exploring"];

const initial = {
  first_name: "", last_name: "", email: "", phone: "", preferred_contact: "",
  property_address: "", parish: "", property_type: "", bedrooms: "", bathrooms: "",
  max_guests: "", pool: "", waterfront: "", current_rental: "",
  airbnb_url: "", vrbo_url: "", nightly_rate: "", monthly_bookings: "",
  biggest_challenge: "", timeline: "",
};

export default function Assessment() {
  usePageMeta("Get a Property Assessment", "Request a professional assessment of your Bermuda vacation rental property — honest insight, revenue potential and a custom management plan.");
  const [form, setForm] = useState(initial);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (s) =>
    setServices((list) => (list.includes(s) ? list.filter((x) => x !== s) : [...list, s]));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("property_assessment", { ...form, services_interested: services });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <main className="flex min-h-screen items-center bg-navy text-ivory">
        <div className="mx-auto max-w-2xl px-6 py-32 text-center" data-testid="assessment-success">
          <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-bermuda" />
          <h1 className="mt-8 font-serif text-4xl sm:text-5xl font-light leading-tight">
            Thank You.<br /><span className="italic text-bermuda">Let's Take Care of Your Property.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-md text-base text-ivory/70">
            Your property assessment request has been received. A member of our local Bermuda team will review your
            property details and reach out shortly.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/consultation" data-testid="assessment-success-consultation-btn" className="bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory">
              Schedule a Consultation
            </Link>
            <Link to="/" data-testid="assessment-success-home-btn" className="border border-ivory/40 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ivory hover:text-navy">
              Return Home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <section className="bg-sand-light">
        <div className="mx-auto max-w-[1100px] px-6 pb-16 pt-40 lg:px-10">
          <SectionHead
            overline="Property Assessment"
            title={<>Let's understand<br /><span className="italic text-bermuda-dark">your property.</span></>}
            copy="Complete this as fully as you can — the more we know, the more specific and useful your assessment will be. No obligation, no pressure."
          />
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-6 pb-28 lg:px-10">
        <Reveal>
          <form onSubmit={onSubmit} className="border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12" data-testid="property-assessment-form">
            <h3 className="font-serif text-xl text-navy">About You</h3>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <Field label="First Name" required><Input data-testid="assessment-first-name" required value={form.first_name} onChange={set("first_name")} /></Field>
              <Field label="Last Name" required><Input data-testid="assessment-last-name" required value={form.last_name} onChange={set("last_name")} /></Field>
              <Field label="Email" required><Input data-testid="assessment-email" required type="email" value={form.email} onChange={set("email")} /></Field>
              <Field label="Phone" required><Input data-testid="assessment-phone" required type="tel" value={form.phone} onChange={set("phone")} /></Field>
              <Field label="Preferred Contact Method" required>
                <Select data-testid="assessment-preferred-contact" required value={form.preferred_contact} onChange={set("preferred_contact")}>
                  <option value="">Select…</option><option>Email</option><option>Phone call</option><option>WhatsApp / Text</option>
                </Select>
              </Field>
            </div>

            <h3 className="mt-12 font-serif text-xl text-navy">About Your Property</h3>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <Field label="Property Address / Area"><Input data-testid="assessment-address" value={form.property_address} onChange={set("property_address")} placeholder="Street or area" /></Field>
              <Field label="Parish" required>
                <Select data-testid="assessment-parish" required value={form.parish} onChange={set("parish")}>
                  <option value="">Select…</option>{PARISHES.map((p) => <option key={p}>{p}</option>)}
                </Select>
              </Field>
              <Field label="Property Type" required>
                <Select data-testid="assessment-property-type" required value={form.property_type} onChange={set("property_type")}>
                  <option value="">Select…</option>{PROPERTY_TYPES.map((p) => <option key={p}>{p}</option>)}
                </Select>
              </Field>
              <Field label="Max Guest Capacity"><Input data-testid="assessment-max-guests" type="number" min="0" value={form.max_guests} onChange={set("max_guests")} /></Field>
              <Field label="Bedrooms" required><Input data-testid="assessment-bedrooms" required type="number" min="0" value={form.bedrooms} onChange={set("bedrooms")} /></Field>
              <Field label="Bathrooms" required><Input data-testid="assessment-bathrooms" required type="number" min="0" step="0.5" value={form.bathrooms} onChange={set("bathrooms")} /></Field>
              <Field label="Pool?" required>
                <Select data-testid="assessment-pool" required value={form.pool} onChange={set("pool")}>
                  <option value="">Select…</option><option>Yes</option><option>No</option>
                </Select>
              </Field>
              <Field label="Waterfront?" required>
                <Select data-testid="assessment-waterfront" required value={form.waterfront} onChange={set("waterfront")}>
                  <option value="">Select…</option><option>Yes</option><option>No</option><option>Water view</option>
                </Select>
              </Field>
            </div>

            <h3 className="mt-12 font-serif text-xl text-navy">Current Rental Activity</h3>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <Field label="Currently a Vacation Rental?" required>
                <Select data-testid="assessment-current-rental" required value={form.current_rental} onChange={set("current_rental")}>
                  <option value="">Select…</option><option>Yes — operating now</option><option>Previously, not currently</option><option>No — never rented</option>
                </Select>
              </Field>
              <Field label="Avg. Nightly Rate (if operating)"><Input data-testid="assessment-nightly-rate" value={form.nightly_rate} onChange={set("nightly_rate")} placeholder="e.g. $350" /></Field>
              <Field label="Airbnb Listing URL"><Input data-testid="assessment-airbnb-url" value={form.airbnb_url} onChange={set("airbnb_url")} placeholder="https://" /></Field>
              <Field label="VRBO Listing URL"><Input data-testid="assessment-vrbo-url" value={form.vrbo_url} onChange={set("vrbo_url")} placeholder="https://" /></Field>
              <Field label="Avg. Monthly Bookings"><Input data-testid="assessment-monthly-bookings" value={form.monthly_bookings} onChange={set("monthly_bookings")} placeholder="e.g. 6" /></Field>
              <Field label="When Would You Like to Begin?" required>
                <Select data-testid="assessment-timeline" required value={form.timeline} onChange={set("timeline")}>
                  <option value="">Select…</option>{TIMELINES.map((t) => <option key={t}>{t}</option>)}
                </Select>
              </Field>
            </div>

            <h3 className="mt-12 font-serif text-xl text-navy">Services You're Interested In</h3>
            <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="assessment-services-group">
              {SERVICE_OPTIONS.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleService(s)}
                  data-testid={`assessment-service-${s.toLowerCase().replace(/\s/g, "-")}`}
                  className={`border px-4 py-3.5 text-left text-sm transition-all duration-300 ${
                    services.includes(s)
                      ? "border-navy bg-navy text-ivory"
                      : "border-navy/15 bg-white text-navy/70 hover:border-bermuda"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="mt-10">
              <Field label="Your Current Biggest Challenge" required>
                <Textarea data-testid="assessment-challenge" required value={form.biggest_challenge} onChange={set("biggest_challenge")} placeholder="e.g. Inconsistent cleaners, no time for guests, I live overseas…" />
              </Field>
            </div>

            <div className="mt-10">
              <SubmitButton loading={loading} testid="property-assessment-form-submit">Request My Property Assessment</SubmitButton>
              <p className="mt-4 text-xs text-navy/45">Your details are used only to prepare your assessment and contact you. No obligation.</p>
            </div>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
